import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { Menu, LogOut, Keyboard, Hash, ChevronDown, Smartphone } from "lucide-react";

import { logoutSuccess } from "../redux/actions/login.action";
import { setChannelPanelOpen, toggleUnlinkedCount } from "../redux/actions/channel.action";
import { selectShowUnlinkedCount, selectChannelById } from "../redux/selectors/channel.selectors";
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import NotificationCenter from './NotificationCenter';
import ChannelSearch from './ChannelSearch';
import { ModeToggle } from './mode-toggle';
import { useMobile } from "../hooks/use-mobile";
import useCanvasStore from "../stores/canvasStore";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const AppBarComponent = () => {
    const location = useLocation();
    const { channelId } = useParams();
    const user = useSelector((state) => state.auth.user);
    const showUnlinkedCount = useSelector(selectShowUnlinkedCount);
    const currentChannel = useSelector(state => selectChannelById(state, channelId));
    const channelName = currentChannel?.name || '';

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const mobileScreen = useMobile();
    const [showShortcutsModal, setShowShortcutsModal] = useState(false);

    // Get setFocusMode and currentFolder from canvas store
    const setFocusMode = useCanvasStore((state) => state.setFocusMode);
    const currentFolder = useCanvasStore((state) => state.currentFolder);
    const bookmarkViewActive = useCanvasStore((state) => state.bookmarkViewActive);
    const setArchiveStatus = useCanvasStore((state) => state.setArchiveStatus);

    // Navigate to last viewed folder if available
    const canvasPath = currentFolder?.id ? `/folder/${currentFolder.id}` : '/canvas';

    // Determine if we're in channel view
    const isChannelView = location.pathname.startsWith('/channels');

    const handleLogout = () => {
        localStorage.clear();
        Cookies.remove("access_token");
        dispatch(logoutSuccess());
        navigate('/login');
    };

    const handleMenuClick = () => {
        dispatch(setChannelPanelOpen(true));
    };

    const handleToggleUnlinkedCount = (checked) => {
        dispatch(toggleUnlinkedCount(checked));
    };

    const handleCanvasClick = async () => {
        setFocusMode(false);
        // Leaving the bookmark view (the Link switches the URL, which switches the view)
        if (bookmarkViewActive) {
            await setArchiveStatus('all');
        }
    };

    return (
        <TooltipProvider>
            <header className="bg-card border-b border-border h-12 md:h-10 min-h-[48px] md:min-h-[40px] max-h-12 md:max-h-10 sticky top-0 z-40">
                <div className="w-full max-w-[1920px] mx-auto md:px-6 h-full">
                    <div className="flex justify-end md:justify-between items-center h-full py-0">
                        {/* Left section */}
                        {mobileScreen && isChannelView ? (
                            /* Mobile Channel View: Menu + Channel name + Search */
                            <div className="flex items-center gap-1 flex-1">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={handleMenuClick}
                                            className="text-foreground hover:bg-accent hover:text-accent-foreground p-1!"
                                        >
                                            <Menu className="h-5 w-5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Channels</TooltipContent>
                                </Tooltip>
                                <h6 className="text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[140px] text-foreground">
                                    {channelName || 'Channels'}
                                </h6>
                                <ChannelSearch />
                            </div>
                        ) : (
                            /* Desktop: Nav links + channel-specific items */
                            <div className="hidden md:flex items-center gap-1">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setShowShortcutsModal(true)}
                                            className="text-foreground hover:bg-accent hover:text-accent-foreground p-1! md:p-0.5!"
                                        >
                                            <Keyboard className="h-5 w-5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Keyboard Shortcuts</TooltipContent>
                                </Tooltip>
                                <div className="flex gap-0.5">
                                    {user?.username && (
                                        <Link
                                            to={canvasPath}
                                            onClick={handleCanvasClick}
                                            className="text-foreground px-2 py-0.5 text-sm capitalize hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
                                        >
                                            Canvas
                                        </Link>
                                    )}
                                    {user?.username && (
                                        <Link
                                            to="/search"
                                            className="text-foreground px-2 py-0.5 text-sm capitalize hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
                                        >
                                            Search
                                        </Link>
                                    )}
                                    {user?.username && (
                                        <Link
                                            to="/channels"
                                            className="text-foreground px-2 py-0.5 text-sm capitalize hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
                                        >
                                            Channels
                                        </Link>
                                    )}
                                </div>

                                {/* Channel-specific: Search, channel name, unlinked toggle */}
                                {isChannelView && (
                                    <>
                                        <div className="ml-3">
                                            <ChannelSearch />
                                        </div>

                                        {channelName && (
                                            <span className="ml-3 text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px] text-muted-foreground flex items-center gap-1">
                                                <Hash className="h-3 w-3" />
                                                {channelName}
                                            </span>
                                        )}

                                        <div className="flex items-center gap-1.5 ml-2">
                                            <Checkbox
                                                id="unlinked-count"
                                                checked={showUnlinkedCount}
                                                onCheckedChange={handleToggleUnlinkedCount}
                                                className="border-input data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground h-3.5 w-3.5"
                                            />
                                            <Label
                                                htmlFor="unlinked-count"
                                                className="text-[0.7rem] text-muted-foreground cursor-pointer"
                                            >
                                                Unlinked
                                            </Label>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Hidden grow div for mobile */}
                        <div className="grow hidden"></div>

                        {/* Right section: Download App, Notifications (channel only), Theme Toggle, User dropdown */}
                        <div className="flex items-center gap-1">
                            {/* Download Android App - Always visible */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Link
                                        to="/download"
                                        className="relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-linear-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 rounded-full shadow-md hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105 group overflow-hidden"
                                    >
                                        {/* Shine effect */}
                                        <span className="absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                                        <Smartphone className="h-4 w-4 relative z-10" />
                                        <span className="hidden sm:inline relative z-10">Get Android App</span>
                                        <span className="sm:hidden relative z-10">App</span>
                                    </Link>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="bg-linear-to-r from-emerald-500 to-teal-500 text-white border-0">
                                    <p className="font-semibold">📱 Download Android App</p>
                                    <p className="text-xs text-white/80">Scan QR or download directly</p>
                                </TooltipContent>
                            </Tooltip>

                            {user?.username && (
                                <>
                                    <NotificationCenter />
                                    <ModeToggle />
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                className="flex items-center gap-2 px-2 py-1 h-auto hover:bg-accent hover:text-accent-foreground"
                                            >
                                                <Avatar className="w-6 h-6">
                                                    <AvatarImage src={user.picture} alt={user.first_name} />
                                                    <AvatarFallback className="text-xs bg-muted text-muted-foreground">
                                                        {user.first_name?.[0]}{user.last_name?.[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="hidden md:inline text-sm whitespace-nowrap text-foreground">
                                                    {user.first_name} {user.last_name}
                                                </span>
                                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-fit">
                                            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
                                                <LogOut className="h-4 w-4" />
                                                <span>Logout</span>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <KeyboardShortcutsModal
                    open={showShortcutsModal}
                    onClose={() => setShowShortcutsModal(false)}
                />
            </header>
        </TooltipProvider>
    );
};

export default AppBarComponent;
